import { useState } from "react";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

export default function ContactPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;

    setFormStatus("sending");
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (typeof value === "string") data.append(key, value);
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d7a0o1jll6jbq2vddv90", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#2B2B2B] font-sans">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 bg-[#F9F7F5]">
        <div className="max-w-[1200px] mx-auto">
          <p className="text-[#A66A3F] text-xs font-semibold tracking-[0.2em] uppercase mb-3">Bize Ulaşın</p>
          <h1 className="text-4xl md:text-5xl font-bold text-[#2B2B2B] leading-tight mb-4">İletişim</h1>
          <p className="text-gray-500 text-base max-w-xl">
            Projenize özel enerji depolama çözümleri için mühendislik ekibimizle iletişime geçin.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">

          {/* Left: Info */}
          <div>
            <h2 className="text-2xl font-bold mb-8">İletişim Bilgileri</h2>

            <div className="flex flex-col gap-6 mb-12">
              {[
                {
                  icon: "ri-map-pin-line",
                  title: "Adres",
                  lines: ["İnönü Mahallesi Fatih Sultan Mehmet Bulvarı No:412 B/81", "Yenimahalle 06370 Ankara, Türkiye"],
                },
                {
                  icon: "ri-phone-line",
                  title: "Telefon",
                  lines: ["+90 312 000 00 00"],
                },
                {
                  icon: "ri-mail-line",
                  title: "E-posta",
                  lines: ["info@aksayenerji.com"],
                },
                {
                  icon: "ri-time-line",
                  title: "Çalışma Saatleri",
                  lines: ["Pazartesi – Cuma: 08:30 – 17:30"],
                },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#F5EDE6] rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#A66A3F] text-lg`} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{item.title}</p>
                    {item.lines.map((line) => (
                      <p key={line} className="text-sm text-[#2B2B2B]">{line}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="w-full h-56 rounded-lg overflow-hidden border border-gray-100">
              <iframe
                title="AKSESS Konum"
                src="https://maps.google.com/maps?q=Fatih+Sultan+Mehmet+Bulvar%C4%B1+No%3A412+Yenimahalle+Ankara+T%C3%BCrkiye&output=embed&z=16"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right: Form */}
          <div>
            <h2 className="text-2xl font-bold mb-8">Mesaj Gönderin</h2>

            {formStatus === "success" ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full">
                  <i className="ri-check-line text-green-500 text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-[#2B2B2B]">Mesajınız İletildi!</h3>
                <p className="text-gray-500 text-sm max-w-xs">En kısa sürede size geri dönüş yapacağız.</p>
                <button
                  onClick={() => setFormStatus("idle")}
                  className="mt-4 text-sm text-[#A66A3F] underline cursor-pointer"
                >
                  Yeni mesaj gönder
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Ad Soyad *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Adınız Soyadınız"
                      className="border border-gray-200 rounded-md px-4 py-3 text-sm text-[#2B2B2B] placeholder-gray-300 focus:outline-none focus:border-[#A66A3F] transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">E-posta *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="ornek@sirket.com"
                      className="border border-gray-200 rounded-md px-4 py-3 text-sm text-[#2B2B2B] placeholder-gray-300 focus:outline-none focus:border-[#A66A3F] transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Şirket</label>
                  <input
                    type="text"
                    name="company"
                    placeholder="Şirket adı (opsiyonel)"
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm text-[#2B2B2B] placeholder-gray-300 focus:outline-none focus:border-[#A66A3F] transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Konu *</label>
                  <select
                    name="subject"
                    required
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm text-[#2B2B2B] focus:outline-none focus:border-[#A66A3F] transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Konu seçin</option>
                    <option value="Teknik Bilgi">Teknik Bilgi Talebi</option>
                    <option value="Fiyat Teklifi">Fiyat Teklifi</option>
                    <option value="Proje Danışmanlık">Proje Danışmanlık</option>
                    <option value="Servis & Bakım">Servis &amp; Bakım</option>
                    <option value="Diğer">Diğer</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Mesaj * <span className={`ml-1 font-normal ${charCount > 450 ? "text-red-400" : "text-gray-400"}`}>({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Projeniz hakkında kısaca bilgi verin..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm text-[#2B2B2B] placeholder-gray-300 focus:outline-none focus:border-[#A66A3F] transition-colors resize-none"
                  />
                </div>

                {formStatus === "error" && (
                  <p className="text-red-500 text-sm">Bir hata oluştu. Lütfen tekrar deneyin.</p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === "sending"}
                  className="mt-2 px-8 py-3.5 bg-[#A66A3F] text-white text-sm font-semibold hover:bg-[#8f5a33] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {formStatus === "sending" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      Mesaj Gönder
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
